/**
 * The Business page's feed, straight from pv-auth:
 *   GET /provider/admin/business[?refresh=1]   (SUPER_ADMIN)
 * Shapes mirror src/services/business/business.service.ts in pv-auth-service.
 * Every null is "not measured" and renders as the words, never as 0. Every
 * Stripe number is labelled with the instant it was fetched.
 */
import { NOT_MEASURED } from './format';

export interface Rate { n: number; of: number; rate: number | null }
export interface WeekCohort { weekStart: string; signups: number; firstCheck: Rate; dayTwo: Rate; d7: Rate; d30: Rate }
export interface MonthRow {
  month: string; complete: boolean; startCount: number; newCount: number; churnedCount: number; churnedFromStart: number;
  churnRate: number | null; startMrrCents: number; churnedMrrCents: number; grossRevenueChurn: number | null;
}
export interface Revenue {
  activeSubscriptions: number; payingMembers: number; trialing: number; pastDue: number;
  mrrCents: number; arrCents: number;
  byPrice: { priceId: string; interval: string | null; listCents: number | null; subscriptions: number; mrrCents: number }[];
  month: MonthRow; months: MonthRow[];
  churn: { monthsMeasured: number; monthlyChurn: number | null };
  ltv: { arpuCents: number | null; ltvCents: number | null; note: string | null; caveat: boolean };
  trials: { started: number; open: number; converted: number; conversion: number | null };
  money: {
    collected30dCents: number; collected90dCents: number; collectedLifetimeCents: number; netLifetimeCents: number;
    refunds: { count: number; cents: number; last90dCount: number; last90dCents: number; lastAt: string | null };
  };
  canceled90d: number;
  customers: number;
  unlinkedSubscriptions: number;
}
export interface FoundingSubscriber {
  userId: string | null; name: string | null; email: string | null;
  /** 'coupon': a founding coupon is on the subscription now; 'intro': the legacy founding intro (first month), list price since. Absent on an older server. */
  kind?: 'coupon' | 'intro';
  interval: string | null; listCents: number | null; payingCents: number | null;
  since: string; renewsAt: string | null; endsAtPeriodEnd: boolean; discountEndsAt: string | null; discountDuration: string | null;
}
export interface Business {
  generatedAt: string;
  accounts: { members: number; teamExcluded: number; byMethod: Record<'email' | 'google' | 'apple' | 'facebook' | 'unknown', number>; byRole: Record<string, number> };
  stripe: { fetchedAt: string | null; error: string | null; cacheSeconds: number };
  revenue: Revenue | null;
  store: { measured: boolean; reason: string | null; payingMembers: number | null; trialing: number | null; mrrCents: number | null; webhookEvents: number };
  paying: { web: number | null; store: number | null; total: number | null };
  founding: {
    codesEndOn: string | null;
    subscribers: FoundingSubscriber[] | null;
    program: { userId: string; name: string | null; email: string | null; status: string; invitedAt: string; compEndsAt: string | null }[];
  };
  cohorts: { weeks: WeekCohort[]; totals: { signups: number; firstCheck: Rate; dayTwo: Rate; d7: Rate; d30: Rate } };
}

export async function fetchBusiness(refresh = false): Promise<Business> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/business${refresh ? '?refresh=1' : ''}`, true)) as Business;
}

/* ── formatting ─────────────────────────────────────────────────────────── */

/** Cents as "$44.99"; the words when not measured. */
export const usd = (cents: number | null | undefined): string =>
  typeof cents === 'number' && Number.isFinite(cents) ? `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : NOT_MEASURED;

/** A 0..1 rate as "14.3%"; the words when there was no denominator. */
export const percent = (rate: number | null | undefined, digits = 1): string =>
  typeof rate === 'number' && Number.isFinite(rate) ? `${(rate * 100).toFixed(digits).replace(/\.0+$/, '')}%` : NOT_MEASURED;

/** "3 of 7 (43%)", or "none eligible yet" when the window has not closed for anyone. */
export const rateLabel = (r: Rate | null | undefined, emptyLabel = 'none eligible yet'): string => {
  if (!r) return NOT_MEASURED;
  if (r.of === 0) return emptyLabel;
  return `${r.n} of ${r.of} (${percent(r.rate, 0)})`;
};

/** "Oct 2026" from a month's first instant (UTC). */
export const monthLabel = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** "2026-10-14", or the words. */
export const ymd = (iso: string | null | undefined, absent = NOT_MEASURED): string => (iso ? iso.slice(0, 10) : absent);

export const INTERVAL_LABEL: Record<string, string> = { month: 'monthly', year: 'annual', week: 'weekly', day: 'daily' };

/** Founding subscribers on the founding price now, and those who had the legacy founding intro. */
export const foundingSplit = (fs: FoundingSubscriber[] | null) =>
  fs ? { coupon: fs.filter((f) => f.kind !== 'intro').length, intro: fs.filter((f) => f.kind === 'intro').length } : null;

/** What a founding subscriber keeps, in words: a forever coupon never ends; a dated one ends on its date. */
export const foundingEnds = (f: FoundingSubscriber): string => {
  if (f.endsAtPeriodEnd) return `cancelled, ends ${ymd(f.renewsAt)}`;
  if (f.kind === 'intro') return 'founding intro at sign-up, list price since';
  if (f.discountEndsAt) return `founding price ends ${ymd(f.discountEndsAt)}`;
  if (f.discountDuration === 'forever') return 'founding price for as long as the subscription runs';
  return 'founding price end not recorded';
};

/* ── definitions, shown on hover ────────────────────────────────────────── */

export type MetricKey =
  | 'mrr' | 'arr' | 'paying' | 'newMonth' | 'churnedMonth' | 'churnRate' | 'grossRevenueChurn' | 'arpu' | 'ltv'
  | 'trialToPaid' | 'founding' | 'refunds' | 'collected' | 'store' | 'members' | 'signups' | 'firstCheck' | 'dayTwo' | 'd7' | 'd30';

export const DEFINITIONS: Record<MetricKey, string> = {
  mrr: 'Monthly recurring revenue: what every active or past-due web subscription bills, after any coupon still running, with annual plans divided by 12. From Stripe at the time shown. Trials, comps, team and QA accounts are left out. App Store and Google Play are not included unless the store line says they are measured.',
  arr: 'Annual run rate: MRR times 12. What a year of today\'s subscriptions would bring in if nobody joined or left.',
  paying: 'Paying members: distinct people with a web subscription that is active or past due right now (Stripe). Trials, comps, team and QA accounts are not counted. Store subscribers are added only when the RevenueCat webhook is delivering.',
  newMonth: 'Subscriptions that started paying this calendar month (UTC): the start date, or the end of the trial when there was one.',
  churnedMonth: 'Subscriptions that ended this calendar month (UTC). A cancellation counts when the paid period runs out, not when the button is pressed.',
  churnRate: 'Monthly churn rate: of the subscriptions paying on the 1st of the month, the share that ended during it. This month runs to now, so it can still rise. Null when nobody was paying on the 1st.',
  grossRevenueChurn: 'Gross revenue churn: the MRR of the subscriptions that ended this month (of those paying on the 1st), divided by the MRR on the 1st. Upgrades and new members do not offset it.',
  arpu: 'Average revenue per paying member: MRR divided by paying members.',
  ltv: 'Lifetime value: ARPU divided by the monthly churn rate, with churn pooled over the complete months in the table (ended over paying at the start). With under 3 complete months of data it carries a caveat; with no churn at all it cannot be estimated and shows why.',
  trialToPaid: 'Trial to paid: of the Stripe trials that have ended, the share followed by a paid invoice on the same subscription. Trials still running are not counted either way.',
  founding: 'Founding members: paying subscriptions that carry the founding coupon now (FOUNDING $2 off monthly, FOUNDINGYEAR $60 off annual, both forever), with when each renews and whether the founding price ever ends. Members who had the earlier founding intro (first month only) are listed apart. Below them, the founding member program (comps) and when each comp runs out.',
  refunds: 'Refunds that succeeded in Stripe, customers only: how many and how much, all time and in the last 90 days.',
  collected: 'Money collected: paid Stripe invoices, customers only, in the last 30 and 90 days and all time; net is after refunds.',
  store: 'App Store and Google Play subscriptions reach us only through the RevenueCat webhook. While it has never delivered, store members and revenue are not measured here.',
  members: 'Accounts that are not deleted and are not team or QA (every @posture.vision address, brian@doctorhutcheson.com, qa-*, +shadow aliases and the configured QA list). Sign-up method is the account\'s first: Google, Apple, Facebook, then email and password.',
  signups: 'Accounts created in the week (Monday to Sunday, UTC), team and QA left out.',
  firstCheck: 'Of the week\'s sign-ups, how many made at least one measured check: an onboarding baseline or a daily check result (replayed checks left out).',
  dayTwo: 'Of the week\'s sign-ups, how many checked on a second, different day (any time after sign-up).',
  d7: 'Day 7 retention: did a check or an Active Mode session on days 7 to 9 after sign-up. Counted only for people whose window has closed or who came back in it; the rest are not eligible yet.',
  d30: 'Day 30 retention: did a check or an Active Mode session on days 30 to 36 after sign-up. Counted only for people whose window has closed or who came back in it.',
};
